import _ from 'lodash';
import moment, { Moment } from 'moment-timezone';
import { BookingStatus, ShiftSlotStatus } from 'utilities/enum-utils';
import { IRosterBooking, IRosterCustomer, IRosterShift, IRosterWorker } from '../types';
import { MantineColor } from '@good/ui/core';

/** Extract a list of unique customers from a list of bookings. */
export function uniqueCustomers(bookingsArray: IRosterBooking[]): IRosterCustomer[] {
  return _.chain(bookingsArray)
    .map((b) => ({
      customerId: b.bookerUserId,
      customerFirstName: b.customerFirstName,
      customerLastName: b.customerLastName,
      customerAvatarUrl: b.attachmentUrl,
    }))
    .uniqBy((c) => c.customerId)
    .sortBy((c) => c.customerFirstName)
    .value();
}

/** Extract a list of unique workers from a list of shifts. */
export function uniqueWorkers(shiftsArray: IRosterShift[]): IRosterWorker[] {
  return _.chain(shiftsArray)
    .map((s) => ({
      supportWorkerId: s.supportWorkerId!,
      workerFirstName: s.workerFirstName!,
      workerLastName: s.workerLastName!,
      workerAvatarUrl: s.workerAttachmentUrl!,
    }))
    .filter((s) => !_.isEmpty(s.supportWorkerId))
    .uniqBy((s) => s.supportWorkerId)
    .sortBy((s) => s.workerFirstName)
    .value();
}

/** Generate an array of hours based on the start/end hour. eg 0-23 = ['12 AM', '1 AM','2 AM'...'10 PM', '11 PM'] */
export function generateHours(startHour: number, endHour: number, dateFormat = 'h A') {
  const hoursArray: number[] = [];
  if (startHour > endHour) {
    hoursArray.push(startHour); // If an invalid start/end input is passed
  } else {
    for (let i = startHour; i <= endHour; i++) {
      hoursArray.push(i);
    }
  }
  // Set the hours as 12 AM, 1 AM... etc
  return hoursArray.map((hour) => moment().set('hour', hour).set('minute', 0).format(dateFormat));
}

/** Retrieves the entire week (or more) from a given day. */
export const getEntireWeekForDay = (day: Date | Moment, number = 7) =>
  _.times(number, (i) => moment(day).add(i, 'days'));

export function collateDuplicateShifts(shifts) {
  // Group shifts into services
  const shiftsByServices = _.groupBy(shifts, function (shift) {
    return shift.serviceDateTimeId + '-' + shift.bookingId;
  });

  // get keys for the grouped services
  const services = _.keys(shiftsByServices);

  return _.chain(services)
    .map((service) => {
      // Finally, group everything inside by StartDateTime
      const groupByDates = _.groupBy(shiftsByServices[service], 'shiftStartDateTime');

      return _.chain(groupByDates)
        .keys()
        .map((key) => ({
          // Get the first occurring row of the object.
          ..._.head(groupByDates[key]),
          // Append a new property showing how many duplicates for this shift
          duplicateCount: _.size(groupByDates[key]),
        }))
        .value();
    })
    .flatten()
    .value();
}

// Groups the bookings into multiple arrays without overlaps.
export function groupWithoutOverlaps(data: any, { startDateKey = 'startDateTime', endDateKey = 'endDateTime' }) {
  // Local function to detect if there are any overlaps between start/end dates for two bookings.
  function isOverlapping(a, b) {
    return (
      (a[startDateKey] >= b[startDateKey] && a[startDateKey] <= b[endDateKey]) ||
      (a[endDateKey] >= b[startDateKey] && a[endDateKey] <= b[endDateKey]) ||
      (b[startDateKey] >= a[startDateKey] && b[startDateKey] <= a[endDateKey]) ||
      (b[endDateKey] >= a[startDateKey] && b[endDateKey] <= a[endDateKey])
    );
  }

  // Working stack
  const stack = _.sortBy([...data], [startDateKey]);

  // Final output
  let results = [];

  while (stack.length > 0) {
    const current = stack.shift();

    let insertedIntoGroup = false;

    for (let i = 0; i < results.length; i++) {
      // Check if there's a place to insert (no overlaps) inside the group.
      const noClashesInGroup = _.every(results[i], (item) => !isOverlapping(item, current));

      if (noClashesInGroup) {
        // No overlaps found! Inserting into this group.
        results[i].push(current);
        insertedIntoGroup = true;
        break;
      }
    }

    // Couldn't find a group to insert into. Create a new group.
    if (!insertedIntoGroup) {
      results = [...results, [current]];
    }
  }

  return results;
}

export function getBookingTheme(bookingStatus: BookingStatus) {
  let bgColor: `${MantineColor}.${string}` | undefined = 'ocean.0';
  let borderColor: `var(--mantine-color-${string}-${string})` | undefined = 'var(--mantine-color-ocean-1)';
  let textTagTheme: 'dark' | 'light' = 'dark';
  let textTagColor = 'blue';
  let textTagWeight: 'regular' | 'bold' = 'regular';

  let statusLabel = _.chain(bookingStatus).capitalize().replace('_', ' ').replace('-', ' ').value();

  switch (bookingStatus) {
    case BookingStatus.PENDING:
      bgColor = undefined;
      borderColor = undefined;
      textTagTheme = 'dark';
      textTagColor = 'gold';
      textTagWeight = 'bold';
      break;
    case BookingStatus.ACCEPTED:
      bgColor = 'green.0';
      borderColor = 'var(--mantine-color-green-1)';
      textTagTheme = 'dark';
      textTagColor = 'green';
      textTagWeight = 'bold';
      break;
    case BookingStatus.CONFIRMED:
      bgColor = 'ocean.0';
      borderColor = 'var(--mantine-color-ocean-1)';
      textTagTheme = 'dark';
      textTagColor = 'blue';
      textTagWeight = 'bold';
      break;
    case BookingStatus.INPROGRESS:
      bgColor = 'orange.0';
      borderColor = 'var(--mantine-color-orange-1)';
      textTagTheme = 'dark';
      textTagColor = 'orange';
      textTagWeight = 'bold';
      statusLabel = 'In progress';
      break;
    case BookingStatus.COMPLETED:
      bgColor = 'lilac.0';
      borderColor = 'var(--mantine-color-lilac-1)';
      textTagTheme = 'dark';
      textTagColor = 'indigo';
      textTagWeight = 'bold';
      statusLabel = 'Completed';
      break;
    case BookingStatus.CUSTOMER_CANCELLED:
      bgColor = undefined;
      borderColor = undefined;
      textTagTheme = 'light';
      textTagColor = 'red';
      textTagWeight = 'bold';
      break;
    case BookingStatus.CUSTOMER_CANCELLED_WITH_FEE:
      bgColor = undefined;
      borderColor = undefined;
      textTagTheme = 'light';
      textTagColor = 'red';
      textTagWeight = 'bold';
      statusLabel = 'Customer cancelled - fee';
      break;
    case BookingStatus.CUSTOMER_CANCELLED_WITHOUT_FEE:
      bgColor = undefined;
      borderColor = undefined;
      textTagTheme = 'light';
      textTagColor = 'red';
      textTagWeight = 'bold';
      statusLabel = 'Customer cancelled - No fee';
      break;
    case BookingStatus.BUSINESS_CANCELLED:
      bgColor = undefined;
      borderColor = undefined;
      textTagTheme = 'light';
      textTagColor = 'rose';
      textTagWeight = 'bold';
      break;
    case BookingStatus.REJECTED:
      bgColor = 'red.0';
      borderColor = undefined;
      textTagTheme = 'light';
      textTagColor = 'red';
      textTagWeight = 'bold';
      break;
    case BookingStatus.ARCHIVED:
      bgColor = undefined;
      borderColor = undefined;
      textTagTheme = 'light';
      textTagColor = 'secondary';
      break;
  }

  return { bgColor, borderColor, textTagColor, textTagTheme, textTagWeight, statusLabel };
}

export function getShiftSlotTheme(shiftStatus: ShiftSlotStatus) {
  let bgColor: `${MantineColor}.${string}` | undefined = 'ocean.0';
  let borderColor: `var(--mantine-color-${string}-${string})` | undefined = 'var(--mantine-color-ocean-1)';
  let textTagTheme: 'dark' | 'light' = 'dark';
  let textTagColor = 'blue';
  let textTagWeight: 'regular' | 'bold' = 'regular';

  let statusLabel = _.chain(shiftStatus).capitalize().replace('_', ' ').replace('-', ' ').value();

  switch (shiftStatus) {
    case ShiftSlotStatus.PENDING:
      bgColor = undefined;
      borderColor = undefined;
      textTagTheme = 'dark';
      textTagColor = 'gold';
      textTagWeight = 'bold';
      break;
    case ShiftSlotStatus.CONFIRMED:
      bgColor = 'ocean.0';
      borderColor = 'var(--mantine-color-ocean-1)';
      textTagTheme = 'dark';
      textTagColor = 'blue';
      textTagWeight = 'bold';
      break;
    case ShiftSlotStatus.UNASSIGNED:
      bgColor = 'orange.0';
      borderColor = 'var(--mantine-color-orange-1)';
      textTagTheme = 'dark';
      textTagColor = 'orange';
      textTagWeight = 'bold';
      break;
    case ShiftSlotStatus.CHECKED_IN:
      bgColor = 'yellow.0';
      borderColor = 'var(--mantine-color-yellow-1)';
      textTagTheme = 'dark';
      textTagColor = 'gold';
      textTagWeight = 'bold';
      break;
    case ShiftSlotStatus.CHECKED_OUT:
      bgColor = 'green.0';
      borderColor = 'var(--mantine-color-green-1)';
      textTagTheme = 'dark';
      textTagColor = 'green';
      textTagWeight = 'bold';
      break;
    case ShiftSlotStatus.CANCELLED:
      bgColor = 'gray.2';
      borderColor = 'var(--mantine-color-gray-3)';
      textTagTheme = 'light';
      textTagColor = 'red';
      textTagWeight = 'bold';
      break;
    case ShiftSlotStatus.PUBLISHING:
      bgColor = 'teal.0';
      borderColor = 'var(--mantine-color-teal-1)';
      textTagTheme = 'dark';
      textTagColor = 'turquoise';
      textTagWeight = 'bold';
      break;
    case ShiftSlotStatus.CLAIMED:
      bgColor = 'lavender.0';
      borderColor = 'var(--mantine-color-lavender-1)';
      textTagTheme = 'dark';
      textTagColor = 'cobalt';
      textTagWeight = 'bold';
      break;
    case ShiftSlotStatus.REJECTED:
      bgColor = 'pink.0';
      borderColor = 'var(--mantine-color-pink-1)';
      textTagTheme = 'dark';
      textTagColor = 'rose';
      textTagWeight = 'bold';
      break;
    case ShiftSlotStatus.APPROVED:
      bgColor = 'lavender.0';
      borderColor = 'var(--mantine-color-lavender-1)';
      textTagTheme = 'dark';
      textTagColor = 'cobalt';
      textTagWeight = 'bold';
      break;
    case ShiftSlotStatus.NO_SHOW:
      bgColor = 'red.0';
      borderColor = 'var(--mantine-color-red-1)';
      textTagTheme = 'dark';
      textTagColor = 'sepia';
      textTagWeight = 'bold';
      break;
    case ShiftSlotStatus.DECLINED:
      bgColor = 'pink.0';
      borderColor = 'var(--mantine-color-pink-1)';
      textTagTheme = 'dark';
      textTagColor = 'rose';
      textTagWeight = 'bold';
      break;
    case ShiftSlotStatus.CANCELLED_PAID:
      bgColor = undefined;
      borderColor = undefined;
      textTagTheme = 'light';
      textTagColor = 'blue';
      textTagWeight = 'bold';
      statusLabel = 'Cancelled - Paid';
      break;
    case ShiftSlotStatus.CANCELLED_UNPAID:
      bgColor = undefined;
      borderColor = undefined;
      textTagTheme = 'light';
      textTagColor = 'blue';
      textTagWeight = 'bold';
      statusLabel = 'Cancelled - Unpaid';
      break;
    case ShiftSlotStatus.SENT_TO_FINANCE:
      bgColor = 'ocean.0';
      borderColor = 'var(--mantine-color-ocean-1)';
      textTagTheme = 'light';
      textTagColor = 'blue';
      textTagWeight = 'bold';
      statusLabel = 'Sent to finance';
      break;
  }

  return { bgColor, borderColor, textTagColor, textTagTheme, textTagWeight, statusLabel };
}
